import { useEffect, useRef } from "react";
import { Button, Input, Textarea } from "@heroui/react";
import { Controller, useForm } from "react-hook-form";
import { ModalError } from "../modals/modalBuilder";
import { NewRenterHandbook } from "../handbooks/renterHandbook";
import { NewRequisitesHandbook } from "../handbooks/requisitesHandbook";
import { CodependentRenterRequisitesHandbook } from "../handbooks/renterRequisitesHandbook";
import { NewDepositeHandbook } from "../handbooks/paymentHandbook";
import { FileUploadField } from "../fileUpload/fileUploadField";
import {
  AgreementOfficesTable,
  AgreementOfficesTableRef,
} from "./AgreementOfficesTable";
import { toast } from "react-toastify";
import { normalizeDatetime } from "@/utils";

export interface AgreementFormData {
  number: string;
  renterId: number;
  requisiteId: string;
  renterRequisiteId: string;
  description: string;
  startDate: string;
  endDate: string;
  deposit: number | null;
  meetingHours: number;
  scan: string | null;
}

interface AgreementFormProps {
  defaultValues?: Partial<AgreementFormData>;
  agreementId: number;
  initialOffices?: Array<{ officeId: number; price: number }>;
  initialWorkplaces?: Array<{ workplaceId: number; price: number }>;
  onSubmit: (
    data: AgreementFormData,
    officesData?: {
      offices: Array<{ officeId: number; price: number }>;
      workplaces: Array<{ workplaceId: number; price: number }>;
    } | null,
  ) => void;
  onDelete?: () => void;
  isEdit?: boolean;
  modalErrors?: ModalError[];
}

export function AgreementForm({
  defaultValues,
  agreementId,
  initialOffices = [],
  initialWorkplaces = [],
  onSubmit,
  onDelete,
  modalErrors = [],
}: AgreementFormProps) {
  const form = useForm<AgreementFormData>({
    defaultValues: defaultValues as AgreementFormData,
  });
  const officesTableRef = useRef<AgreementOfficesTableRef>(null);

  const watchedRenter = form.watch("renterId");

  useEffect(() => {
    if (modalErrors.length) {
      modalErrors.forEach(({ field, message }) => {
        form.setError(field as keyof AgreementFormData, {
          type: "manual",
          message,
        });
      });
    } else {
      form.clearErrors();
    }
  }, [modalErrors, form]);

  useEffect(() => {
    if (!defaultValues) return;

    Object.entries(defaultValues).forEach(([key, value]) => {
      if (value === undefined) return;

      if (key === "start_date" || key === "end_date") {
        form.setValue(
          key as keyof AgreementFormData,
          normalizeDatetime(value as string),
        );
      } else {
        form.setValue(key as keyof AgreementFormData, value as never);
      }
    });
  }, [defaultValues, form]);

  // Reset renterRequisite and deposit when renter changes
  useEffect(() => {
    if (watchedRenter !== defaultValues?.renterId) {
      form.setValue("renterRequisiteId", "");
      form.setValue("deposit", null);
    }
  }, [watchedRenter, defaultValues?.renterId, form]);

  const handleSubmit = (data: AgreementFormData) => {
    // Получаем данные офисов через ref
    const officesData = officesTableRef.current?.getOfficesData() ?? null;

    // Валидация офисов
    if (officesData === null) {
      toast.error("Необходимо выбрать хотя бы один офис");
      return;
    }

    // Передаем данные формы и офисов в onSubmit
    onSubmit(data, officesData);
  };

  return (
    <form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
      <div className="flex flex-col gap-2 md:w-1/2">
        <Controller
          control={form.control}
          name="number"
          rules={{ required: false }}
          render={({ field }) => (
            <Input
              {...field}
              label="Номер договора"
              placeholder="0"
              variant="bordered"
              isInvalid={!!form.formState.errors.number}
              errorMessage={form.formState.errors.number?.message}
            />
          )}
        />

        <Controller
          control={form.control}
          name="renterId"
          rules={{ required: "Арендатор обязателен для заполнения" }}
          render={({ field }) => (
            <NewRenterHandbook
              selectedKey={field.value ? String(field.value) : ""}
              onSelectionChange={(key) => {
                field.onChange(key ? Number(key) : null);
              }}
              isRequired
              isInvalid={!!form.formState.errors.renterId}
              errorMessage={form.formState.errors.renterId?.message}
              children={<></>}
            />
          )}
        />

        <Controller
          control={form.control}
          name="renterRequisiteId"
          rules={{ required: "Счёт арендатора обязателен для заполнения" }}
          render={({ field }) => (
            <CodependentRenterRequisitesHandbook
              deps={[String(watchedRenter || "")]}
              selectedKey={field.value ? String(field.value) : ""}
              onSelectionChange={(key) => {
                field.onChange(key);
              }}
              isRequired
              isInvalid={!!form.formState.errors.renterRequisiteId}
              errorMessage={form.formState.errors.renterRequisiteId?.message}
              children={<></>}
              isDisabled={!watchedRenter}
            />
          )}
        />

        <Controller
          control={form.control}
          name="requisiteId"
          rules={{ required: "Счёт обязателен для заполнения" }}
          render={({ field }) => (
            <NewRequisitesHandbook
              selectedKey={field.value ? String(field.value) : ""}
              onSelectionChange={(key) => {
                field.onChange(key);
              }}
              isRequired
              isInvalid={!!form.formState.errors.requisiteId}
              errorMessage={form.formState.errors.requisiteId?.message}
              children={<></>}
            />
          )}
        />

        <Controller
          control={form.control}
          name="startDate"
          rules={{ required: "Дата начала обязательна для заполнения" }}
          render={({ field }) => (
            <Input
              {...field}
              value={normalizeDatetime(field.value)}
              type="datetime-local"
              label="Дата начала"
              variant="bordered"
              isRequired
              isInvalid={!!form.formState.errors.startDate}
              errorMessage={form.formState.errors.startDate?.message}
            />
          )}
        />

        <Controller
          control={form.control}
          name="endDate"
          rules={{ required: "Дата окончания обязательна для заполнения" }}
          render={({ field }) => (
            <Input
              {...field}
              value={normalizeDatetime(field.value)}
              type="datetime-local"
              label="Дата окончания"
              variant="bordered"
              isRequired
              isInvalid={!!form.formState.errors.endDate}
              errorMessage={form.formState.errors.endDate?.message}
            />
          )}
        />

        <Controller
          control={form.control}
          name="deposit"
          rules={{ required: false }}
          render={({ field }) => (
            <NewDepositeHandbook
              deps={[String(watchedRenter || "")]}
              selectedKey={field.value ? String(field.value) : ""}
              onSelectionChange={(key) => {
                field.onChange(key ? Number(key) : null);
              }}
              isInvalid={!!form.formState.errors.deposit}
              errorMessage={form.formState.errors.deposit?.message?.toString()}
              isDisabled={!watchedRenter}
              children={<></>}
            />
          )}
        />

        <Controller
          control={form.control}
          name="meetingHours"
          rules={{ required: "Часы на переговорки обязательны для заполнения" }}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value?.toString() || ""}
              onValueChange={(value) => field.onChange(Number(value))}
              type="number"
              label="Часы на переговорки"
              placeholder="0"
              variant="bordered"
              isRequired
              isInvalid={!!form.formState.errors.meetingHours}
              errorMessage={form.formState.errors.meetingHours?.message}
              onChange={(e) => field.onChange(Number(e.target.value))}
            />
          )}
        />

        <Controller
          control={form.control}
          name="description"
          rules={{ required: false }}
          render={({ field }) => (
            <Textarea
              {...field}
              label="Описание"
              placeholder="Описание"
              variant="bordered"
              minRows={3}
              isInvalid={!!form.formState.errors.description}
              errorMessage={form.formState.errors.description?.message}
            />
          )}
        />

        <Controller
          control={form.control}
          name="scan"
          rules={{ required: false }}
          render={({ field }) => (
            <FileUploadField
              label="Скан договора"
              value={field.value || ""}
              onChange={field.onChange}
              isInvalid={!!form.formState.errors.scan}
              errorMessage={form.formState.errors.scan?.message}
            />
          )}
        />
      </div>

      <div className="mb-4 mt-4">
        <AgreementOfficesTable
          ref={officesTableRef}
          agreementId={agreementId}
          initialOffices={initialOffices}
          initialWorkplaces={initialWorkplaces}
          showSaveButton={false}
        />
      </div>

      <div className="flex flex-col md:flex-row gap-2 mt-4">
        <Button type="submit" color="primary" variant="solid">
          Сохранить
        </Button>
        {onDelete && (
          <Button color="danger" variant="bordered" onPress={onDelete}>
            Удалить
          </Button>
        )}
      </div>
    </form>
  );
}
